import React, { useRef } from "react";
import { motion } from "framer-motion";
import { VictoryPhase } from "./types";
import LiquidityCounter from "./LiquidityCounter";
import { useTheme } from "../../contexts/ThemeContext";

interface FightMeterProps {
  position: number;
  victoryPhase?: VictoryPhase;
  winner?: "A" | "B";
  liquidityA?: number;
  liquidityB?: number;
  showLiquidity?: boolean;
  className?: string;
}

// Animation configuration constants
const OVERDRIVE_ANIMATION = {
  sizePattern: ["2rem", "2.3rem", "2.2rem", "2.6rem", "2.7rem", "3.3rem"],
  sizeTransition: {
    duration: 1.2,
    repeat: 0,
    ease: "easeIn" as const,
  },
  xTransition: {
    duration: 0.1,
    repeat: 8,
    ease: "easeOut" as const,
  },
  rotateTransition: {
    duration: 0.8,
    ease: "easeOut" as const,
  },
  backgroundTransition: {
    duration: 0.8,
  },
};

const GRADIENT_TRANSITION = {
  elimination: { duration: 0.2, ease: "easeOut" as const },
  victory: { duration: 0.5, ease: "easeOut" as const },
  idle: { duration: 0.3, ease: "easeInOut" as const },
};

const MORPHING_SPHERE_CONFIGS = [
  {
    filter: "morph1",
    xPattern: [0, -1, 1, -2, 2, -1, 1, 0],
    rotateDir: 360,
    rotateDuration: 6,
  },
  {
    filter: "morph2",
    xPattern: [0, 1, -1, 2, -2, 1, -1, 0],
    rotateDir: -360,
    rotateDuration: 11,
  },
];

// SVG Morphing Filters Component
const MorphingFilters: React.FC = () => (
  <svg className="absolute w-0 h-0" aria-hidden="true">
    <defs>
      {/* First morphing filter - slower, larger displacement */}
      <filter id="morph1">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.02 0.03"
          numOctaves="3"
          seed="5"
        >
          <animate
            attributeName="baseFrequency"
            dur="1s"
            values="0.02 0.03;0.04 0.02;0.03 0.05;0.05 0.03;0.02 0.03"
            repeatCount="indefinite"
          />
        </feTurbulence>
        <feColorMatrix in="turbulence" type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncA type="discrete" tableValues="1 1" />
          <feFuncR type="table" tableValues="0.7 0.9 1 0.5 0.2" />
          <feFuncG type="table" tableValues="0.2 0.5 1 0.5 0.2" />
        </feComponentTransfer>
        <feGaussianBlur stdDeviation="2" />
        <feDisplacementMap
          in="SourceGraphic"
          scale="6"
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>

      {/* Second morphing filter - faster, smaller displacement */}
      <filter id="morph2">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.03 0.02"
          numOctaves="4"
          seed="0"
        >
          <animate
            attributeName="baseFrequency"
            dur="0.5s"
            values="0.03 0.02;0.02 0.04;0.04 0.03;0.03 0.02"
            repeatCount="indefinite"
          />
        </feTurbulence>
        <feColorMatrix in="turbulence" type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncA type="discrete" tableValues="1 1" />
          <feFuncR type="table" tableValues="0.2 0.5 1 0.9 0.7" />
          <feFuncG type="table" tableValues="0.3 0.6 1 0.6 0.3" />
        </feComponentTransfer>
        <feGaussianBlur stdDeviation="0.7" />
        <feDisplacementMap
          in="SourceGraphic"
          scale="5"
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
    </defs>
  </svg>
);

const FightMeter: React.FC<FightMeterProps> = ({
  position,
  victoryPhase = "idle",
  winner = "A",
  liquidityA = 0,
  liquidityB = 0,
  showLiquidity = false,
  className = "",
}) => {
  const { colors } = useTheme();
  const isTokenAEliminated = victoryPhase !== "idle" && winner === "B";
  const isTokenBEliminated = victoryPhase !== "idle" && winner === "A";
  const isTokenADead = isTokenAEliminated && victoryPhase === "complete";
  const isTokenBDead = isTokenBEliminated && victoryPhase === "complete";

  const meterRef = useRef<HTMLDivElement>(null);
  
  // Color constants using theme colors
  const COLORS = {
    tokenA: {
      primary: colors.teamA.primary,
      bright: colors.teamA.bright,
      dark: colors.teamA.dark,
    },
    tokenB: {
      primary: colors.teamB.primary,
      bright: colors.teamB.bright,
      dark: colors.teamB.dark,
    },
    blend: "#ffffff",
    glow: "rgba(255, 255, 255, 1)",
    meterBackground: "#1e293b",
  };

  // Determine victory-enhanced colors
  const getEnhancedColors = () => {
    if (victoryPhase === "idle") {
      return {
        tokenA: COLORS.tokenA.dark,
        tokenAMid: COLORS.tokenA.primary,
        tokenB: COLORS.tokenB.dark,
        tokenBMid: COLORS.tokenB.primary,
        tokenABright: COLORS.tokenA.bright,
        tokenBBright: COLORS.tokenB.bright,
        blendColor: COLORS.blend,
        glowIntensity: 1,
      };
    }

    // Glow should be strongest during meter-overdrive, then winner takes over
    const isOverdrive = victoryPhase === "meter-overdrive";
    const isVictory =
      victoryPhase === "elimination" ||
      victoryPhase === "transfer" ||
      victoryPhase === "complete";

    if (isVictory) {
      // After meter-overdrive, show only winner's colors
      const winnerColor = winner === "A" ? COLORS.tokenA : COLORS.tokenB;

      return {
        tokenA: winner === "A" ? winnerColor.dark : "#1e293b",
        tokenAMid: winner === "A" ? winnerColor.primary : "#1e293b",
        tokenB: winner === "B" ? winnerColor.dark : "#1e293b",
        tokenBMid: winner === "B" ? winnerColor.primary : "#1e293b",
        tokenABright: winner === "A" ? winnerColor.bright : "#0f172a",
        tokenBBright: winner === "B" ? winnerColor.bright : "#0f172a",
        blendColor: winner === "A" ? winnerColor.bright : winnerColor.bright,
        glowIntensity: 0.5,
      };
    }

    if (isOverdrive) {
      // Maximum glow during overdrive, loser starts fading to gray
      return {
        tokenA: winner === "A" ? COLORS.tokenA.primary : "#4b5563",
        tokenAMid: winner === "A" ? COLORS.tokenA.bright : "#374151",
        tokenB: winner === "B" ? COLORS.tokenB.primary : "#4b5563",
        tokenBMid: winner === "B" ? COLORS.tokenB.bright : "#374151",
        tokenABright: winner === "A" ? "#ffffff" : "#374151",
        tokenBBright: winner === "B" ? "#ffffff" : "#374151",
        blendColor: "#ffffff",
        glowIntensity: 3,
      };
    }

    // Default idle state
    return {
      tokenA: COLORS.tokenA.dark,
      tokenAMid: COLORS.tokenA.primary,
      tokenB: COLORS.tokenB.dark,
      tokenBMid: COLORS.tokenB.primary,
      tokenABright: COLORS.tokenA.bright,
      tokenBBright: COLORS.tokenB.bright,
      blendColor: COLORS.blend,
      glowIntensity: 1,
    };
  };

  const enhancedColors = getEnhancedColors();

  return (
    <div className={`relative ${className}`}>
      {/* Main meter container - no overflow-hidden here */}
      <div className="relative">
        {/* Meter background with clipped gradient */}
        <motion.div
          ref={meterRef}
          className="relative h-8 sm:h-9 md:h-10 border-[0.125rem] rounded-full overflow-hidden"
          style={{
            backgroundColor: COLORS.meterBackground,
            borderColor: COLORS.meterBackground,
          }}
        >
          {/* Base gradient layer */}
          <motion.div
            className="absolute inset-0"
            animate={{
              background:
                (victoryPhase === "elimination" ||
                  victoryPhase === "transfer" ||
                  victoryPhase === "complete") &&
                winner
                  ? `linear-gradient(to right, ${
                      winner === "A"
                        ? enhancedColors.tokenA
                        : enhancedColors.tokenB
                    } 0%, ${
                      winner === "A"
                        ? enhancedColors.tokenABright
                        : enhancedColors.tokenBBright
                    } 100%)`
                  : `linear-gradient(to right, 
                ${enhancedColors.tokenA} 0%, 
                ${enhancedColors.tokenAMid} ${Math.max(0, position - 20)}%, 
                ${enhancedColors.tokenABright} ${Math.max(0, position - 8)}%,
                ${enhancedColors.blendColor} ${position}%,
                ${enhancedColors.tokenBBright} ${Math.min(100, position + 8)}%,
                ${enhancedColors.tokenBMid} ${Math.min(100, position + 20)}%, 
                ${enhancedColors.tokenB} 100%)`,
            }}
            transition={
              victoryPhase === "meter-overdrive"
                ? { duration: 1.2, ease: "easeIn" as const } // Fade over overdrive duration
                : victoryPhase === "elimination"
                ? GRADIENT_TRANSITION.elimination
                : victoryPhase !== "idle"
                ? GRADIENT_TRANSITION.victory
                : GRADIENT_TRANSITION.idle
            }
          />
          
          {/* Vertical gradient overlay for depth */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(to bottom, 
                rgba(255,255,255,0.15) 0%, 
                rgba(255,255,255,0.05) 20%, 
                rgba(0,0,0,0) 50%, 
                rgba(0,0,0,0.1) 80%,
                rgba(0,0,0,0.2) 100%)`
            }}
          />

          {/* Horizontal energy beam during overdrive */}
          {victoryPhase === "meter-overdrive" &&
            (() => {
              // Use the actual position directly
              const beamWidth =
                winner === "A"
                  ? position // From 0% to position
                  : 100 - position; // From position to 100%
              const beamLeft = winner === "A" ? "0%" : `${position}%`;

              return (
                <motion.div className="absolute inset-0 flex items-center pointer-events-none">
                  {/* Main energy beam with radial gradient */}
                  <motion.div
                    className="absolute"
                    initial={{
                      width: `${beamWidth}%`,
                      background:
                        winner === "A"
                          ? `radial-gradient(ellipse 100% 50% at left center, 
                          rgba(255,255,255,1) 0%, 
                          rgba(255,255,255,1) 30%,
                          rgba(255,255,255,0.95) 50%,
                          rgba(255,255,255,0.9) 70%,
                          rgba(255,255,255,0.5) 90%,
                          transparent 100%)`
                          : `radial-gradient(ellipse 100% 50% at right center, 
                          rgba(255,255,255,1) 0%, 
                          rgba(255,255,255,1) 30%,
                          rgba(255,255,255,0.95) 50%,
                          rgba(255,255,255,0.9) 70%,
                          rgba(255,255,255,0.5) 90%,
                          transparent 100%)`,
                      height: "0.25rem",
                      opacity: 0,
                      left: beamLeft,
                    }}
                    animate={{
                      opacity: [0, 1, 1],
                      height: ["0.25rem", "2.5rem", "2rem"],
                      filter: ["blur(1px)", "blur(0px)", "blur(0.2px)"],
                    }}
                    transition={{
                      duration: 1.2,
                      ease: "easeInOut",
                      times: [0, 0.8, 1],
                    }}
                  />
                </motion.div>
              );
            })()}

          {/* Inner shadow for depth */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              boxShadow:
                "inset 0 0.125rem 0.25rem rgba(0, 0, 0, 0.3), inset 0 -0.125rem 0.25rem rgba(0, 0, 0, 0.3)",
            }}
          />
        </motion.div>

        {/* Glowing white sphere indicator with morphing shape */}
        <motion.div
          className="absolute top-0 bottom-0 left-0 right-0 z-10 pointer-events-none"
          initial={{
            left: `${position}%`,
            x: "-50%",
            opacity: 1,
          }}
          animate={{
            left: `${position}%`,
            x: "-50%",
            opacity:
              victoryPhase === "idle" || victoryPhase === "meter-overdrive"
                ? 1
                : 0,
          }}
          transition={{
            type: "tween",
            ease: victoryPhase === "meter-overdrive" ? "easeInOut" : "backOut",
            duration: victoryPhase === "meter-overdrive" ? 1.2 : 0.3,
            opacity: { duration: 0.3, ease: "easeOut" },
          }}
        >
          <div className="relative h-full flex items-center justify-center">
            {/* SVG filters for organic morphing patterns */}
            <MorphingFilters />

            {/* Two overlapping morphing spheres with rotation */}
            {MORPHING_SPHERE_CONFIGS.map((config) => (
              <motion.div
                key={config.filter}
                className="absolute w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full"
                style={{
                  background: `radial-gradient(circle, #ffffff 0%, #ffffff 40%, rgba(255,255,255,0.8) 60%, rgba(255,255,255,0) 100%)`,
                  boxShadow: `0 0 2rem rgba(255,255,255,0.9), 0 0 4rem rgba(255,255,255,0.6)`,
                  filter: `url(#${config.filter})`,
                }}
                animate={
                  victoryPhase === "meter-overdrive"
                    ? {
                        width: OVERDRIVE_ANIMATION.sizePattern,
                        height: OVERDRIVE_ANIMATION.sizePattern,
                        x: config.xPattern,
                        rotate: [0, config.rotateDir],
                      }
                    : {
                        rotate: [0, config.rotateDir, 0],
                      }
                }
                transition={
                  victoryPhase === "meter-overdrive"
                    ? {
                        width: OVERDRIVE_ANIMATION.sizeTransition,
                        height: OVERDRIVE_ANIMATION.sizeTransition,
                        x: OVERDRIVE_ANIMATION.xTransition,
                      }
                    : {
                        rotate: {
                          duration: config.rotateDuration,
                          repeat: Infinity,
                          ease: "easeInOut",
                        },
                      }
                }
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Liquidity counters */}
      {showLiquidity && (
        <>
          <LiquidityCounter
            value={liquidityA}
            side="left"
            victoryPhase={victoryPhase}
            isEliminated={isTokenAEliminated}
            isDead={isTokenADead}
          />
          <LiquidityCounter
            value={liquidityB}
            side="right"
            victoryPhase={victoryPhase}
            isEliminated={isTokenBEliminated}
            isDead={isTokenBDead}
          />
        </>
      )}
    </div>
  );
};

export default FightMeter;
